import React, { useContext } from 'react';
import { ReportingSetting, QcdrSettings, ProviderReportingStatus } from 'common/lib/model/macra/QcdrSettings';
import Flex from '../Flex';
import Checkbox from '../Checkbox';
import Table from '../Table';
import { CellInfo } from 'react-table';
import QcdrSettingsStore from 'src/stores/QcdrSettingsStore';
import { PqrsProvider } from 'common/lib/model/macra/PqrsProvider';
import Card from '../Card';
import styles from './styles.module.scss';
import Select from 'react-select';
import { styleSelectComponent } from 'src/utils';
import { toJS } from 'mobx';

const ELIGIBILITY_OPTIONS = [
    { value: 'eligible', label: 'Eligible' },
    { value: 'optInEligible', label: 'Opt In Eligible' },
    { value: 'notEligible', label: 'Not Eligible' },
];

const ELIGIBLE_REPORTING_OPTIONS = [
    { value: 'report', label: 'Report', group: 'eligible' },
    { value: 'doNotReport', label: 'Do Not Report', group: 'eligible' },
];

const OPT_IN_ELIGIBLE_REPORTING_OPTIONS = [
    { value: 'optIn', label: 'Opt In', group: 'optInEligible' },
    { value: 'doNotReport', label: 'Do Not Report', group: 'optInEligible' },
];

const NOT_ELIGIBLE_REPORTING_OPTIONS = [
    { value: 'volunteer', label: 'Volunteer', group: 'notEligible' },
    { value: 'doNotReport', label: 'Do Not Report', group: 'notEligible' },
];

interface Props {
    settings: QcdrSettings['reportingSettings'];
    onSettingChange: (setting: ReportingSetting) => void;
}

function ReportingSettingsStep(props: Props) {
    const qcdrSettingStore = useContext(QcdrSettingsStore);

    const getReportingOptions = (eligibility: string) => {
        switch (eligibility) {
            case 'eligible':
                return ELIGIBLE_REPORTING_OPTIONS;
            case 'optInEligible':
                return OPT_IN_ELIGIBLE_REPORTING_OPTIONS;
            case 'notEligible':
                return NOT_ELIGIBLE_REPORTING_OPTIONS;
            default:
                return [];
        }
    };

    return (
        <div>
            <Flex direction="row">
                <Flex value={1}>
                    <h3>STEP 2: REPORTING SETTINGS</h3>
                </Flex>
            </Flex>
            {props.settings.map((setting, index) => {
                const tinSettingForThisReportingSetting = qcdrSettingStore.tinSettings.find((tinSetting) => {
                    if (tinSetting.submitForFacility && tinSetting.applyTinToAll) {
                        return tinSetting.groupTin === setting.tin;
                    }

                    return tinSetting.submitForFacility;
                });

                if (tinSettingForThisReportingSetting) {
                    return (
                        <Card
                            label={`TIN: ${setting.tin}`}
                            subLabel={
                                <Flex gap={8} align="center">
                                    <Select
                                        onChange={(s: any) => {
                                            props.onSettingChange({
                                                ...setting,
                                                providers: setting.providers.map<ProviderReportingStatus>((status) => {
                                                    return {
                                                        ...status,
                                                        eligibility: s.value,
                                                    };
                                                }),
                                            });
                                        }}
                                        options={ELIGIBILITY_OPTIONS}
                                        placeholder="Set All Eligibility"
                                        styles={styleSelectComponent()}
                                        className={styles.setAll}
                                        menuPortalTarget={document.body}
                                    />
                                    <Select
                                        onChange={(s: any) => {
                                            props.onSettingChange({
                                                ...setting,
                                                providers: setting.providers.map<ProviderReportingStatus>((status) => {
                                                    return status.eligibility === s.group
                                                        ? {
                                                              ...status,
                                                              reportingAction: s.value,
                                                          }
                                                        : status;
                                                }),
                                            });
                                        }}
                                        options={[
                                            { label: 'Eligible', options: ELIGIBLE_REPORTING_OPTIONS },
                                            { label: 'Opt In Eligible', options: OPT_IN_ELIGIBLE_REPORTING_OPTIONS },
                                            { label: 'Not Eligible', options: NOT_ELIGIBLE_REPORTING_OPTIONS },
                                        ]}
                                        placeholder="Set All Actions"
                                        styles={styleSelectComponent()}
                                        className={styles.setAll}
                                        menuPortalTarget={document.body}
                                    />
                                </Flex>
                            }
                            key={setting.tin}
                            className={styles.cardWrap}
                        >
                            <Flex direction="column" className={styles.contentWrap}>
                                <Checkbox
                                    data-test-id="GroupReportingChkBox"
                                    checked={setting.reportingType === 'group'}
                                    label="Group Reporting"
                                    onChange={(e) => {
                                        props.onSettingChange({
                                            ...setting,
                                            reportingType: e.target.checked ? 'group' : 'ind',
                                        });
                                    }}
                                />

                                <Table
                                    key={'table_' + index}
                                    isLoading={false}
                                    columns={[
                                        {
                                            Header: 'PROVIDER NAME',
                                            accessor: 'npi',
                                            minWidth: 150,
                                            Cell: (props: CellInfo) =>
                                                qcdrSettingStore.getProviderByNpi(props.value) && (
                                                    <span>
                                                        {
                                                            qcdrSettingStore.getProviderByNpi(props.value)
                                                                .providerLastName
                                                        }
                                                        ,{' '}
                                                        {
                                                            qcdrSettingStore.getProviderByNpi(props.value)
                                                                .providerFirstName
                                                        }
                                                    </span>
                                                ),
                                        },
                                        {
                                            Header: 'NPI',
                                            accessor: 'npi',
                                            minWidth: 150,
                                            Cell: (props: CellInfo) => <span>{props.value}</span>,
                                        },
                                        {
                                            Header: 'TYPE',
                                            accessor: 'npi',
                                            minWidth: 150,
                                            Cell: (props: CellInfo) =>
                                                qcdrSettingStore.getProviderByNpi(props.value) && (
                                                    <span>
                                                        {qcdrSettingStore.getProviderByNpi(props.value).providerType}
                                                    </span>
                                                ),
                                        },
                                        {
                                            Header: 'CASE COUNT',
                                            accessor: 'npi',
                                            Cell: (props: CellInfo) => {
                                                const allProviderInstances = qcdrSettingStore.pqrsProviders
                                                    .filter(
                                                        (provider: PqrsProvider) =>
                                                            provider.providerNpi === props.value,
                                                    )
                                                    .map((provider: PqrsProvider) =>
                                                        provider.providerCaseCount
                                                            ? parseInt(provider.providerCaseCount)
                                                            : 0,
                                                    );
                                                const providerCaseCount = allProviderInstances.reduce(
                                                    (a: number, b: number) => a + b,
                                                    0,
                                                );
                                                return <span>{providerCaseCount}</span>;
                                            },
                                        },
                                        {
                                            Header: 'ELIGIBILITY',
                                            accessor: 'eligibility',
                                            minWidth: 125,
                                            Cell: (cellProps: CellInfo) => (
                                                <Select
                                                    value={ELIGIBILITY_OPTIONS.find(
                                                        (option) => option.value === cellProps.value,
                                                    )}
                                                    onChange={(s: any) => {
                                                        props.onSettingChange({
                                                            ...setting,
                                                            providers: setting.providers.map<ProviderReportingStatus>(
                                                                (status) => {
                                                                    return status.npi === cellProps.original.npi
                                                                        ? {
                                                                              ...status,
                                                                              eligibility: s.value,
                                                                          }
                                                                        : status;
                                                                },
                                                            ),
                                                        });
                                                    }}
                                                    options={ELIGIBILITY_OPTIONS}
                                                    placeholder="Select Eligibility"
                                                    styles={styleSelectComponent()}
                                                    menuPortalTarget={document.body}
                                                />
                                            ),
                                        },
                                        {
                                            Header: 'REPORTING ACTION',
                                            accessor: 'reportingAction',
                                            minWidth: 125,
                                            Cell: (cellProps: CellInfo) => {
                                                const options = getReportingOptions(cellProps.original.eligibility);

                                                return (
                                                    <Select
                                                        value={options.find(
                                                            (option) => option.value === cellProps.value,
                                                        )}
                                                        onChange={(s: any) => {
                                                            props.onSettingChange({
                                                                ...setting,
                                                                providers: setting.providers.map<
                                                                    ProviderReportingStatus
                                                                >((status) => {
                                                                    return status.npi === cellProps.original.npi
                                                                        ? {
                                                                              ...status,
                                                                              reportingAction: s.value,
                                                                          }
                                                                        : status;
                                                                }),
                                                            });
                                                        }}
                                                        options={options}
                                                        placeholder="Select Action"
                                                        styles={styleSelectComponent()}
                                                        menuPortalTarget={document.body}
                                                    />
                                                );
                                            },
                                        },
                                    ]}
                                    data={setting.providers}
                                />
                            </Flex>
                        </Card>
                    );
                }
            })}
        </div>
    );
}

export default ReportingSettingsStep;
